import { FLOSS, flossByCode, toPaletteEntry, luminance, type FlossColor } from '../data/dmc';
import type { DesignBrief } from '../brief/types';
import { makeRng, normalizeSeed, type Rng } from './rng';
import type { KnotType } from './knots';
import { leadStrandOrder } from './normal';
import { renderWord } from './font';
import { quantizeToAlpha } from './alpha';
import { chainLengthCm } from './chain';
import { bodyLengthCm, DEFAULT_GAUGE, rowsForLength, type Gauge } from './thread';
import {
  knotsInRow,
  knotLeftPosition,
  type AlphaPattern,
  type ChainModule,
  type ChainPattern,
  type ColorIndex,
  type Design,
  type MotifKind,
  type NormalPattern,
  type PaletteEntry,
  type Sizing,
} from './types';

/**
 * Design generation.
 *
 * Everything here is a pure function of (brief, seed). No clocks, no
 * Math.random, no network. That is what makes a design shareable as a URL: the
 * link carries the design itself, and regenerating the option list from the
 * same brief and seed reproduces the same five cards in the same order.
 */

export interface Candidate {
  design: Design;
  /** Short reason this option exists, shown on the card. */
  rationale: string;
}

/* -------------------------------------------------------------------------- */
/* Normal-pattern knot grids                                                  */
/* -------------------------------------------------------------------------- */

type KnotChooser = (row: number, pairLeftPosition: number, strandCount: number) => KnotType;

function buildRows(strandCount: number, rowCount: number, choose: KnotChooser): KnotType[][] {
  const rows: KnotType[][] = [];
  for (let r = 0; r < rowCount; r++) {
    const row: KnotType[] = [];
    for (let k = 0; k < knotsInRow(strandCount, r); k++) {
      row.push(choose(r, knotLeftPosition(r, k), strandCount));
    }
    rows.push(row);
  }
  return rows;
}

/**
 * Chevron: everything on the left half knots rightwards, everything on the
 * right half knots leftwards, so strings walk in towards the centre and the
 * visible knots form nested V shapes.
 */
const chevronChooser: KnotChooser = (_r, p, n) => (p + 1 <= n / 2 ? 'F' : 'B');

/** The same idea reversed: strings walk outwards, so the V points up. */
const invertedChooser: KnotChooser = (_r, p, n) => (p + 1 <= n / 2 ? 'B' : 'F');

/** Candy stripe: every knot forward, so every colour walks all the way across. */
const stripeChooser: KnotChooser = () => 'F';

/** Candy stripe leaning the other way. */
const stripeBackChooser: KnotChooser = () => 'B';

/**
 * Diamonds: alternate between walking in and walking out. Half a period is the
 * number of rows a string needs to cross from the edge to the centre, which is
 * strandCount / 2, so a full diamond is strandCount rows tall.
 */
function diamondChooser(halfPeriod: number): KnotChooser {
  return (r, p, n) => {
    const outward = Math.floor(r / halfPeriod) % 2 === 1;
    return outward ? invertedChooser(r, p, n) : chevronChooser(r, p, n);
  };
}

/* -------------------------------------------------------------------------- */
/* Colour assignment                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Decide which strand starts where so that the visible colour sequence comes
 * out in the order she asked for.
 *
 * This is not cosmetic fiddling. In a diagonal-knot bracelet the order in
 * which colours appear as diagonals is a *consequence* of the knot grid and
 * the string circulation, not something an author picks. Laying the strings out
 * naively (colour 1, colour 2, colour 3...) produces a real bracelet whose
 * stripes come out in a scrambled order. So: simulate the grid, find out which
 * strand leads each diagonal, and lay the colours onto those strands.
 */
function assignColors(
  strandCount: number,
  rows: KnotType[][],
  desired: ColorIndex[],
  mirrored: boolean,
): ColorIndex[] {
  const probe: NormalPattern = {
    style: 'normal',
    strandColors: Array.from({ length: strandCount }, (_, i) => i),
    rows,
  };
  const leads = leadStrandOrder(probe);
  const out = new Array<ColorIndex>(strandCount).fill(desired[0] ?? 0);

  if (!mirrored) {
    const assigned = new Set<number>();
    leads.forEach((strand, i) => {
      out[strand] = desired[i % desired.length]!;
      assigned.add(strand);
    });
    // Any strand that never leads still needs a colour; continue the cycle.
    let next = leads.length;
    for (let s = 0; s < strandCount; s++) {
      if (!assigned.has(s)) out[s] = desired[next++ % desired.length]!;
    }
    return out;
  }

  // Mirrored patterns (chevrons, diamonds) pair strand j with strand n-1-j.
  const half = strandCount / 2;
  const pairIndex = (j: number) => Math.min(j, strandCount - 1 - j);
  const halfColors = new Array<ColorIndex | undefined>(half).fill(undefined);
  let cursor = 0;
  for (const strand of leads) {
    const pi = pairIndex(strand);
    if (halfColors[pi] === undefined) {
      halfColors[pi] = desired[cursor++ % desired.length]!;
    }
    if (cursor >= half) break;
  }
  for (let i = 0; i < half; i++) {
    if (halfColors[i] === undefined) halfColors[i] = desired[cursor++ % desired.length]!;
  }
  for (let j = 0; j < strandCount; j++) out[j] = halfColors[pairIndex(j)]!;
  return out;
}

/* -------------------------------------------------------------------------- */
/* Palette helpers                                                            */
/* -------------------------------------------------------------------------- */

function paletteFromCodes(codes: string[]): { palette: PaletteEntry[]; floss: FlossColor[] } {
  const floss = codes.map((c) => flossByCode(c)).filter((f): f is FlossColor => !!f);
  const safe = floss.length ? floss : [FLOSS[22]!, FLOSS[25]!, FLOSS[15]!, FLOSS[45]!];
  return { palette: safe.map(toPaletteEntry), floss: safe };
}

/** Index of the lightest / darkest colour, for choosing readable pairings. */
function extremeIndex(floss: FlossColor[], which: 'light' | 'dark'): number {
  let best = 0;
  for (let i = 1; i < floss.length; i++) {
    const better =
      which === 'light' ? floss[i]!.light > floss[best]!.light : floss[i]!.light < floss[best]!.light;
    if (better) best = i;
  }
  return best;
}

/** Ensure text is legible: pick the two palette entries furthest apart in tone. */
function highestContrastPair(floss: FlossColor[]): [number, number] {
  let best: [number, number] = [0, Math.min(1, floss.length - 1)];
  let bestDelta = -1;
  for (let i = 0; i < floss.length; i++) {
    for (let j = 0; j < floss.length; j++) {
      if (i === j) continue;
      const d = Math.abs(luminance(floss[i]!.hex) - luminance(floss[j]!.hex));
      if (d > bestDelta) {
        bestDelta = d;
        best = [i, j];
      }
    }
  }
  return best;
}

/* -------------------------------------------------------------------------- */
/* Individual generators                                                      */
/* -------------------------------------------------------------------------- */

interface GenContext {
  brief: DesignBrief;
  seed: string;
  rng: Rng;
  gauge: Gauge;
  palette: PaletteEntry[];
  floss: FlossColor[];
  sizing: Sizing;
  bodyCm: number;
}

/**
 * Snap a row count to a whole number of pattern repeats.
 *
 * Nearest, not up: always rounding up made every design overshoot by up to a
 * full repeat — a 12-row chevron repeat can add nearly 2 cm to a 16 cm
 * bracelet, which is a visibly loose fit. At least one whole repeat is always
 * kept, because half a chevron is not a pattern.
 */
function roundToRepeat(rows: number, period: number): number {
  if (period <= 0) return rows;
  return Math.max(period, Math.round(rows / period) * period);
}

function makeNormal(
  ctx: GenContext,
  opts: {
    name: string;
    blurb: string;
    rationale: string;
    strandCount: number;
    chooser: KnotChooser;
    mirrored: boolean;
    period: number;
    colorOrder?: ColorIndex[];
  },
): Candidate {
  const rawRows = rowsForLength(ctx.bodyCm, ctx.gauge.normalRowsPerCm);
  const rowCount = roundToRepeat(rawRows, opts.period);
  const rows = buildRows(opts.strandCount, rowCount, opts.chooser);
  const desired = opts.colorOrder ?? ctx.palette.map((_, i) => i);
  const strandColors = assignColors(opts.strandCount, rows, desired, opts.mirrored);

  return {
    design: {
      v: 1,
      name: opts.name,
      blurb: opts.blurb,
      palette: ctx.palette,
      sizing: ctx.sizing,
      seed: ctx.seed,
      body: { style: 'normal', strandColors, rows },
    },
    rationale: opts.rationale,
  };
}

function makeAlphaFromGrid(
  ctx: GenContext,
  opts: {
    name: string;
    blurb: string;
    rationale: string;
    grid: ColorIndex[][];
    leader: ColorIndex;
    baseFallback: ColorIndex;
  },
): Candidate | null {
  if (opts.grid.length === 0 || opts.grid[0]!.length === 0) return null;
  const hexes = ctx.palette.map((p) => p.hex);
  const q = quantizeToAlpha(opts.grid, hexes, opts.leader);
  const baseColors = q.baseColors.map((c) => (c === opts.leader ? opts.baseFallback : c));

  const body: AlphaPattern = {
    style: 'alpha',
    grid: q.grid,
    baseColors,
    leaderColors: new Array<ColorIndex>(q.grid.length).fill(opts.leader),
    firstRowDirection: 'ltr',
  };

  return {
    design: {
      v: 1,
      name: opts.name,
      blurb: opts.blurb,
      palette: ctx.palette,
      sizing: ctx.sizing,
      seed: ctx.seed,
      body,
    },
    rationale: opts.rationale,
  };
}

function makeWordAlpha(ctx: GenContext, word: string): Candidate | null {
  const rendered = renderWord(word, 2, 1);
  if (rendered.rows === 0) return null;

  const [aIdx, bIdx] = highestContrastPair(ctx.floss);
  // The background is the leader, because the leader does the bulk of the work
  // and backgrounds are usually the bigger area.
  const bgIsLight = ctx.floss[aIdx]!.light > ctx.floss[bIdx]!.light;
  const leader = bgIsLight ? aIdx : bIdx;
  const letter = bgIsLight ? bIdx : aIdx;

  const wordBlock: ColorIndex[][] = rendered.grid.map((row) => row.map((on) => (on ? letter : leader)));

  // A word rendered once is only a few centimetres long — "BFF" is about 19
  // rows, which at 7 rows/cm is under 3 cm. A bracelet is 15-20 cm. Without
  // this step the guide would confidently produce a band far too short to go
  // round a wrist, which is exactly the kind of wrong number that costs an
  // evening. So: repeat the word as many whole times as fit, separated by a
  // plain gap, and pad the two ends with plain background.
  const grid = fillLengthWithBlock(wordBlock, rendered.cols, leader, ctx);
  const repeats = countRepeats(wordBlock.length, grid.length);

  return makeAlphaFromGrid(ctx, {
    name: `"${word}" in letters`,
    blurb:
      `The word ${word} knotted in ${ctx.palette[letter]!.name} on a ${ctx.palette[leader]!.name} ` +
      `background, ${rendered.cols} strings wide` +
      (repeats > 1 ? `, repeating ${repeats} times along the bracelet.` : '.'),
    rationale: 'Alpha pattern — the only style that can spell things.',
    grid,
    leader,
    baseFallback: letter,
  });
}

/** Rows of plain background between one repeat of a motif and the next. */
const ALPHA_GAP_ROWS = 5;

/**
 * Tile a block down the length of the bracelet: whole repeats only, a plain gap
 * between them, and any leftover split evenly between the two ends so the word
 * sits centred with tidy margins by the ties.
 */
function fillLengthWithBlock(
  block: ColorIndex[][],
  cols: number,
  background: ColorIndex,
  ctx: GenContext,
): ColorIndex[][] {
  const target = rowsForLength(ctx.bodyCm, ctx.gauge.alphaRowsPerCm);
  const blank = () => new Array<ColorIndex>(cols).fill(background);

  // A single repeat already longer than the wrist: use it as-is rather than
  // truncating a word into nonsense.
  if (block.length >= target) return block.map((r) => [...r]);

  const stride = block.length + ALPHA_GAP_ROWS;
  const repeats = Math.max(1, Math.floor((target + ALPHA_GAP_ROWS) / stride));
  const used = repeats * stride - ALPHA_GAP_ROWS;
  const leftover = Math.max(0, target - used);
  const top = Math.floor(leftover / 2);
  const bottom = leftover - top;

  const out: ColorIndex[][] = [];
  for (let i = 0; i < top; i++) out.push(blank());
  for (let r = 0; r < repeats; r++) {
    if (r > 0) for (let i = 0; i < ALPHA_GAP_ROWS; i++) out.push(blank());
    for (const row of block) out.push([...row]);
  }
  for (let i = 0; i < bottom; i++) out.push(blank());
  return out;
}

function countRepeats(blockRows: number, totalRows: number): number {
  if (blockRows <= 0) return 1;
  return Math.max(1, Math.floor((totalRows + ALPHA_GAP_ROWS) / (blockRows + ALPHA_GAP_ROWS)));
}

/** Small repeating pictures for alpha bracelets when there is no word. */
const ALPHA_MOTIFS: Record<string, string[]> = {
  hearts: ['..#.#..', '.#####.', '.#####.', '..###..', '...#...', '.......'],
  checkers: ['###...#', '###...#', '###...#', '...###.', '...###.', '...###.'],
  waves: ['..##...', '.#..#..', '#....#.', '#....##', '.#..#..', '..##...'],
  dots: ['..#....', '.###...', '..#....', '.....#.', '....###', '.....#.'],
  arrows: ['...#...', '..###..', '.#.#.#.', '...#...', '...#...', '.......'],
};

function makeMotifAlpha(ctx: GenContext, motifKey: string): Candidate | null {
  const tile = ALPHA_MOTIFS[motifKey];
  if (!tile) return null;
  const [aIdx, bIdx] = highestContrastPair(ctx.floss);
  const bgIsLight = ctx.floss[aIdx]!.light > ctx.floss[bIdx]!.light;
  const leader = bgIsLight ? aIdx : bIdx;
  const ink = bgIsLight ? bIdx : aIdx;

  const cols = tile[0]!.length + 4;
  const targetRows = rowsForLength(ctx.bodyCm, ctx.gauge.alphaRowsPerCm);
  const grid: ColorIndex[][] = [];
  for (let r = 0; r < targetRows; r++) {
    const src = tile[r % tile.length]!;
    const row = new Array<ColorIndex>(cols).fill(leader);
    for (let i = 0; i < src.length; i++) if (src[i] === '#') row[i + 2] = ink;
    grid.push(row);
  }

  return makeAlphaFromGrid(ctx, {
    name: `${capitalize(motifKey)} band`,
    blurb: `A repeating ${motifKey} picture in ${ctx.palette[ink]!.name} on ${ctx.palette[leader]!.name}, ${cols} strings wide.`,
    rationale: 'Alpha pattern — a picture grid, one knot per square.',
    grid,
    leader,
    baseFallback: ink,
  });
}

function makeChain(ctx: GenContext, motif: MotifKind): Candidate {
  // Colour roles: the chain runs the whole length, the motif is the eye-catcher,
  // the accent is the little centre dot.
  const lightest = extremeIndex(ctx.floss, 'light');
  const darkest = extremeIndex(ctx.floss, 'dark');

  let chainColor: number;
  let motifColor: number;
  let accentColor: number;

  const greenIdx = ctx.floss.findIndex((f) => f.family === 'green');
  const redIdx = ctx.floss.findIndex((f) => f.family === 'red' || f.family === 'pink');
  const yellowIdx = ctx.floss.findIndex((f) => f.family === 'yellow');

  if (motif === 'cherry' || motif === 'strawberry') {
    chainColor = greenIdx >= 0 ? greenIdx : darkest;
    motifColor = redIdx >= 0 ? redIdx : 0;
    accentColor = motif === 'strawberry' && yellowIdx >= 0 ? yellowIdx : lightest;
  } else if (motif === 'daisy') {
    chainColor = greenIdx >= 0 ? greenIdx : ctx.rng.int(0, ctx.floss.length - 1);
    motifColor = lightest;
    accentColor = yellowIdx >= 0 ? yellowIdx : darkest;
  } else {
    chainColor = lightest;
    motifColor = redIdx >= 0 ? redIdx : darkest;
    accentColor = darkest === motifColor ? lightest : darkest;
  }

  // Make sure the three roles are actually distinguishable.
  if (motifColor === chainColor) motifColor = (chainColor + 1) % ctx.floss.length;
  if (accentColor === motifColor) accentColor = (motifColor + 1) % ctx.floss.length;
  if (accentColor === chainColor && ctx.floss.length > 2) {
    accentColor = (accentColor + 1) % ctx.floss.length;
  }

  const strandColors: ColorIndex[] = [
    chainColor,
    accentColor,
    motifColor,
    motifColor,
    accentColor,
    chainColor,
  ];

  // Build modules until the estimated length covers the wrist.
  const chainKnots = ctx.rng.int(6, 10);
  const rounds = motif === 'strawberry' ? 2 : 1;
  const modules: ChainModule[] = [{ kind: 'chain', knots: chainKnots }];
  let guard = 0;
  while (
    chainLengthCm({ style: 'chain', strandColors, modules }, ctx.gauge.normalRowsPerCm) <
      ctx.bodyCm &&
    guard++ < 60
  ) {
    modules.push({ kind: 'motif', motif, rounds });
    modules.push({ kind: 'chain', knots: chainKnots });
  }

  const motifCount = modules.filter((m) => m.kind === 'motif').length;
  const body: ChainPattern = { style: 'chain', strandColors, modules };

  return {
    design: {
      v: 1,
      name: `${capitalize(motif)} chain`,
      blurb: `${motifCount} ${motif} motif${motifCount === 1 ? '' : 's'} spaced along a ${ctx.palette[chainColor]!.name} chain, ${chainKnots} knots apart.`,
      palette: ctx.palette,
      sizing: ctx.sizing,
      seed: ctx.seed,
      body,
    },
    rationale: 'Motif chain — worked in modules, not rows. Very different feel to a knotted band.',
  };
}

/* -------------------------------------------------------------------------- */
/* The option list                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Produce 3-5 genuinely distinct candidates.
 *
 * "Distinct" is enforced structurally, not by hoping: the pool below spans all
 * three construction styles and several different knot grids, and the selection
 * step never takes two candidates with the same shape signature.
 */
export function generateCandidates(
  brief: DesignBrief,
  seedInput: string,
  gauge: Gauge = DEFAULT_GAUGE,
): Candidate[] {
  const seed = normalizeSeed(seedInput);
  const rng = makeRng(`${seed}|${brief.colors.join(',')}|${brief.stylePreference}|${brief.word ?? ''}`);
  const { palette, floss } = paletteFromCodes(brief.colors);
  const sizing = brief.sizing;
  const ctx: GenContext = {
    brief,
    seed,
    rng,
    gauge,
    palette,
    floss,
    sizing,
    bodyCm: bodyLengthCm(sizing),
  };

  const colorCount = palette.length;
  // Two strings per colour keeps a chevron symmetric; cap the width so the
  // bracelet stays a sensible size for a wrist.
  const chevronStrands = clampEven(colorCount * 2, 6, 16);
  const stripeStrands = clampEven(colorCount * 2, 6, 14);
  const diamondStrands = clampEven(colorCount * 2, 8, 16);

  const pool: (Candidate | null)[] = [];

  pool.push(
    makeNormal(ctx, {
      name: 'Classic chevron',
      blurb: `Nested V shapes, ${chevronStrands} strings, colours arriving in the order you gave them.`,
      rationale: 'The default friendship bracelet. Forgiving, and the shape hides small tension mistakes.',
      strandCount: chevronStrands,
      chooser: chevronChooser,
      mirrored: true,
      period: chevronStrands,
    }),
  );

  pool.push(
    makeNormal(ctx, {
      name: 'Candy stripe',
      blurb: `Simple diagonal stripes running one way across ${stripeStrands} strings.`,
      rationale: 'The easiest of the three normal patterns: every single knot is a forward knot.',
      strandCount: stripeStrands,
      chooser: stripeChooser,
      mirrored: false,
      period: stripeStrands * 2,
    }),
  );

  pool.push(
    makeNormal(ctx, {
      name: 'Diamonds',
      blurb: `Rows walk inwards then outwards, so the colours close into diamonds down the band.`,
      rationale: 'More visual interest than a chevron for the same difficulty of knot.',
      strandCount: diamondStrands,
      chooser: diamondChooser(diamondStrands / 2),
      mirrored: true,
      period: diamondStrands * 2,
    }),
  );

  pool.push(
    makeNormal(ctx, {
      name: 'Inverted chevron',
      blurb: 'The same V shapes as a chevron, pointing the other way.',
      rationale: 'Reads quite differently on the wrist despite being a one-character change.',
      strandCount: chevronStrands,
      chooser: invertedChooser,
      mirrored: true,
      period: chevronStrands,
    }),
  );

  pool.push(
    makeNormal(ctx, {
      name: 'Reverse candy stripe',
      blurb: 'Diagonal stripes leaning the opposite way.',
      rationale: 'All backward knots — good practice if forward knots already feel automatic.',
      strandCount: stripeStrands,
      chooser: stripeBackChooser,
      mirrored: false,
      period: stripeStrands * 2,
    }),
  );

  if (brief.word) pool.push(makeWordAlpha(ctx, brief.word));
  pool.push(makeMotifAlpha(ctx, rng.pick(['hearts', 'checkers', 'waves', 'dots', 'arrows'])));

  const motif: MotifKind = brief.motifs[0] ?? 'daisy';
  pool.push(makeChain(ctx, motif));

  const candidates = pool.filter((c): c is Candidate => c !== null);
  return selectDistinct(candidates, brief, rng);
}

/**
 * Pick 3-5 candidates that genuinely differ, honouring any style preference.
 * Style preference boosts rather than filters, so she is never left with one
 * option because of a stray keyword.
 */
function selectDistinct(all: Candidate[], brief: DesignBrief, rng: Rng): Candidate[] {
  const scored = all.map((c) => {
    let score = 0;
    if (brief.stylePreference !== 'any' && c.design.body.style === brief.stylePreference) score += 10;
    if (brief.word && c.design.body.style === 'alpha') score += 6;
    if (brief.motifs.length && c.design.body.style === 'chain') score += 4;
    // A tiny seeded jitter so different seeds reorder equal-scoring options.
    return { c, score: score + rng.next() };
  });
  scored.sort((a, b) => b.score - a.score);

  const chosen: Candidate[] = [];
  const seenShapes = new Set<string>();
  const seenStyles = new Map<string, number>();

  for (const { c } of scored) {
    const shape = shapeSignature(c);
    if (seenShapes.has(shape)) continue;
    const style = c.design.body.style;
    // Never let one style crowd out the others entirely.
    if ((seenStyles.get(style) ?? 0) >= 3) continue;
    seenShapes.add(shape);
    seenStyles.set(style, (seenStyles.get(style) ?? 0) + 1);
    chosen.push(c);
    if (chosen.length === 5) break;
  }

  // Guarantee at least three options even if the pool was unusually uniform.
  for (const { c } of scored) {
    if (chosen.length >= 3) break;
    if (!chosen.includes(c)) chosen.push(c);
  }
  return chosen;
}

/**
 * A cheap fingerprint of what a design LOOKS like, so two options that differ
 * only in an invisible detail are not both offered.
 */
function shapeSignature(c: Candidate): string {
  const b = c.design.body;
  if (b.style === 'normal') {
    const grid = b.rows
      .slice(0, 12)
      .map((r) => r.join(''))
      .join('|');
    return `normal:${b.strandColors.join(',')}:${grid}`;
  }
  if (b.style === 'alpha') {
    return `alpha:${b.grid
      .slice(0, 16)
      .map((r) => r.join(''))
      .join('|')}`;
  }
  return `chain:${b.strandColors.join(',')}:${b.modules
    .map((m) => (m.kind === 'chain' ? `c${m.knots}` : `m${m.motif}${m.rounds}`))
    .join('')}`;
}

function clampEven(n: number, min: number, max: number): number {
  const even = n % 2 === 0 ? n : n + 1;
  return Math.max(min, Math.min(max, even));
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
